import { NoteRecord } from './note-record';
import { ProvisionAnalysis } from './provision-analysis';

/**
 * Valid workflow phases in advancement order.
 * The backend enforces that phase transitions move ±1 step only (no skipping).
 */
export const WORKFLOW_PHASES = [
  'Phase I - Requested',
  'Phase II - Assembly',
  'Phase III - Review & Approval',
  'Phase IV - Signature',
  'Phase V - Out for Signature',
  'Phase VI - Fully Executed'
] as const;

export type WorkflowPhase = typeof WORKFLOW_PHASES[number];

export interface PhaseHistoryEntry {
  phase: string;
  enteredAt: string;   // ISO 8601
  enteredBy: string;   // email
}

export interface WorkflowExtractedParty {
  name: string;
  role: 'facility' | 'vendor' | 'insurer' | 'unknown';
  confidence: number;
}

/**
 * A WorkflowRequest tracks a document through the intake lifecycle.
 *
 * Fields are marked optional because partial objects are common:
 *   - POST create: client sends only what it has, backend fills the rest
 *   - PUT update: client sends only changed fields
 *   - Old/legacy records may not have every field
 */
export interface WorkflowRequest {
  // Identity
  workflowNum?: string;                  // backend-generated on create (format: "WF-<timestamp>")
  name?: string;

  // Workflow state
  phase?: WorkflowPhase | string;
  status?: string;                       // e.g. "Requested", "Open", "Approved"
  priority?: 'Low' | 'Normal' | 'High' | 'Urgent' | string;
  documentType?: 'contract' | 'baa' | 'coi' | 'addendum' | 'unknown' | string;
  transactionType?: 'New' | 'Renewal' | 'Amendment' | string;

  // People
  requestor?: string | any;              // email (or legacy User object)
  requestorName?: string;
  assignedTo?: string | any | null;

  // Dates
  dateRequested?: string;
  dateCompleted?: string | null;

  // Linked entity (null until the workflow is converted to a real entity)
  entityType?: string | null;
  entityId?: string | null;
  contractFriendlyName?: string | null;

  // File + AI
  s3FileUrl?: string;
  fileName?: string;
  classification?: string;
  classificationConfidence?: number;
  extractedSummary?: string;
  extractedParties?: WorkflowExtractedParty[];
  extractedEffectiveDate?: string | null;
  extractedExpirationDate?: string | null;
  intakeAnalysisResult?: any;            // full AI blob — pass through opaque

  /**
   * Document Review: the AI gap analysis of this request's uploaded document against the
   * approved provisions. Written by the frontend once the analysis completes (same pattern
   * as `contract.provisionAnalysis`) so re-opening the request renders the saved result
   * instead of re-billing an AI run.
   */
  provisionAnalysis?: ProvisionAnalysis | null;

  // Freeform
  description?: string;
  requestNotes?: string;
  notes?: NoteRecord[];

  // History (backend-managed)
  phaseHistory?: PhaseHistoryEntry[];

  // Timestamps (backend-managed)
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Returns the next phase in the workflow, or null if already at the last phase.
 * Returns null if the current phase is not a recognized value.
 */
export function nextPhase(current: string | undefined | null): WorkflowPhase | null {
  if (!current) return null;
  const idx = (WORKFLOW_PHASES as readonly string[]).indexOf(current);
  if (idx < 0 || idx >= WORKFLOW_PHASES.length - 1) return null;
  return WORKFLOW_PHASES[idx + 1];
}

/**
 * Returns the previous phase in the workflow, or null if already at the first phase.
 * Returns null if the current phase is not a recognized value.
 */
export function previousPhase(current: string | undefined | null): WorkflowPhase | null {
  if (!current) return null;
  const idx = (WORKFLOW_PHASES as readonly string[]).indexOf(current);
  if (idx <= 0) return null;
  return WORKFLOW_PHASES[idx - 1];
}
